import { ValidationOperator } from "../API";

export interface ValidationRule {
  id: string;
  name: string;
  operator: ValidationOperator;
  expectedValue?: string;
  customPromptTemplate?: string;
}

export interface ConversationMessage {
  role: "user" | "assistant";
  content: string;
  tool_calls?: Array<{
    type: "function";
    function: {
      name: string;
      arguments: string;
    };
  }>;
  tool_call_id?: string;
}

export interface ValidationResult {
  ruleId: string;
  ruleName: string;
  operator: ValidationOperator;
  passed: boolean;
  message: string;
  expectedValue?: string;
  actualValue?: string;
  error?: string;
}

export interface ConversationValidationResults {
  conversationId: string;
  agentId: string;
  totalRules: number;
  passedRules: number;
  failedRules: number;
  results: ValidationResult[];
  executedAt: Date;
}

/**
 * Client-side validation engine for executing validation rules against conversation data
 */
export class ValidationEngine {
  /**
   * Execute all validation rules against a conversation
   */
  static async executeValidations(
    conversation: ConversationMessage[],
    rules: ValidationRule[],
    conversationId: string,
    agentId: string,
    llmSendFunction?: (prompt: string, messages: any[], tools: any[], isUserMessage: boolean) => Promise<string | null>
  ): Promise<ConversationValidationResults> {
    const results: ValidationResult[] = [];
    
    for (const rule of rules) {
      try {
        const result = await this.executeRule(conversation, rule, llmSendFunction);
        results.push(result);
      } catch (error) {
        results.push({
          ruleId: rule.id,
          ruleName: rule.name,
          operator: rule.operator,
          passed: false,
          message: `Error executing validation: ${error instanceof Error ? error.message : 'Unknown error'}`,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    const passedRules = results.filter(r => r.passed).length;
    const failedRules = results.filter(r => !r.passed).length;

    const finalResults = {
      conversationId,
      agentId,
      totalRules: rules.length,
      passedRules,
      failedRules,
      results,
      executedAt: new Date(),
    };
    
    return finalResults;
  }

  /**
   * Execute a single validation rule against a conversation
   */
  private static async executeRule(
    conversation: ConversationMessage[],
    rule: ValidationRule,
    llmSendFunction?: (prompt: string, messages: any[], tools: any[], isUserMessage: boolean) => Promise<string | null>
  ): Promise<ValidationResult> {
    switch (rule.operator) {
      case ValidationOperator.SHOULD_CONTAIN:
        return this.validateShouldContain(conversation, rule);
      
      case ValidationOperator.SHOULD_NOT_CONTAIN:
        return this.validateShouldNotContain(conversation, rule);
      
      case ValidationOperator.SHOULD_RUN_TOOL:
        return this.validateShouldRunTool(conversation, rule);
      
      case ValidationOperator.SHOULD_NOT_RUN_TOOL:
        return this.validateShouldNotRunTool(conversation, rule);
      
      case ValidationOperator.CUSTOM_LLM_JUDGE:
        return this.validateCustomLLMJudge(conversation, rule, llmSendFunction);
      
      default:
        throw new Error(`Unsupported validation operator: ${rule.operator}`);
    }
  }

  /**
   * Validate that assistant responses contain expected text
   */
  private static validateShouldContain(
    conversation: ConversationMessage[],
    rule: ValidationRule
  ): ValidationResult {
    if (!rule.expectedValue) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: "Expected value is required for SHOULD_CONTAIN validation",
        error: "Missing expected value",
      };
    }

    const assistantMessages = conversation.filter(msg => msg.role === "assistant");
    const combinedResponse = assistantMessages.map(msg => msg.content).join(" ");
    
    const contains = combinedResponse.toLowerCase().includes(rule.expectedValue.toLowerCase());
    
    return {
      ruleId: rule.id,
      ruleName: rule.name,
      operator: rule.operator,
      passed: contains,
      message: contains 
        ? `✓ Response contains expected text: "${rule.expectedValue}"`
        : `✗ Response does not contain expected text: "${rule.expectedValue}"`,
      expectedValue: rule.expectedValue,
      actualValue: combinedResponse,
    };
  }

  /**
   * Validate that assistant responses do NOT contain expected text
   */
  private static validateShouldNotContain(
    conversation: ConversationMessage[],
    rule: ValidationRule
  ): ValidationResult {
    if (!rule.expectedValue) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: "Expected value is required for SHOULD_NOT_CONTAIN validation",
        error: "Missing expected value",
      };
    }

    const assistantMessages = conversation.filter(msg => msg.role === "assistant");
    const combinedResponse = assistantMessages.map(msg => msg.content).join(" ");
    
    const contains = combinedResponse.toLowerCase().includes(rule.expectedValue.toLowerCase());
    
    return {
      ruleId: rule.id,
      ruleName: rule.name,
      operator: rule.operator,
      passed: !contains,
      message: !contains 
        ? `✓ Response correctly does not contain: "${rule.expectedValue}"`
        : `✗ Response incorrectly contains: "${rule.expectedValue}"`,
      expectedValue: rule.expectedValue,
      actualValue: combinedResponse,
    };
  }

  /**
   * Validate that the agent used a specific tool
   */
  private static validateShouldRunTool(
    conversation: ConversationMessage[],
    rule: ValidationRule
  ): ValidationResult {
    if (!rule.expectedValue) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: "Tool name is required for SHOULD_RUN_TOOL validation",
        error: "Missing tool name",
      };
    }

    const assistantMessages = conversation.filter(msg => msg.role === "assistant");
    const toolCalls = assistantMessages.flatMap(msg => msg.tool_calls || []);
    const toolNames = toolCalls.map(call => call.function.name);
    
    const usedTool = toolNames.some(name => 
      name.toLowerCase().includes(rule.expectedValue!.toLowerCase())
    );
    
    return {
      ruleId: rule.id,
      ruleName: rule.name,
      operator: rule.operator,
      passed: usedTool,
      message: usedTool 
        ? `✓ Agent correctly used tool: "${rule.expectedValue}"`
        : `✗ Agent did not use expected tool: "${rule.expectedValue}"`,
      expectedValue: rule.expectedValue,
      actualValue: toolNames.join(", ") || "No tools used",
    };
  }

  /**
   * Validate that the agent did NOT use a specific tool
   */
  private static validateShouldNotRunTool(
    conversation: ConversationMessage[],
    rule: ValidationRule
  ): ValidationResult {
    if (!rule.expectedValue) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: "Tool name is required for SHOULD_NOT_RUN_TOOL validation",
        error: "Missing tool name",
      };
    }

    const assistantMessages = conversation.filter(msg => msg.role === "assistant");
    const toolCalls = assistantMessages.flatMap(msg => msg.tool_calls || []);
    const toolNames = toolCalls.map(call => call.function.name);
    
    const usedTool = toolNames.some(name => 
      name.toLowerCase().includes(rule.expectedValue!.toLowerCase())
    );
    
    return {
      ruleId: rule.id,
      ruleName: rule.name,
      operator: rule.operator,
      passed: !usedTool,
      message: !usedTool 
        ? `✓ Agent correctly did not use tool: "${rule.expectedValue}"`
        : `✗ Agent incorrectly used tool: "${rule.expectedValue}"`,
      expectedValue: rule.expectedValue,
      actualValue: toolNames.join(", ") || "No tools used",
    };
  }

  /**
   * Validate using a custom LLM prompt
   */
  private static async validateCustomLLMJudge(
    conversation: ConversationMessage[],
    rule: ValidationRule,
    llmSendFunction?: (prompt: string, messages: any[], tools: any[], isUserMessage: boolean) => Promise<string | null>
  ): Promise<ValidationResult> {
    if (!rule.customPromptTemplate) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: "Custom prompt template is required for CUSTOM_LLM_JUDGE validation",
        error: "Missing custom prompt template",
      };
    }

    if (!llmSendFunction) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: "LLM send function is required for CUSTOM_LLM_JUDGE validation",
        error: "Missing LLM send function",
      };
    }

    const assistantMessages = conversation.filter(msg => msg.role === "assistant");
    const combinedResponse = assistantMessages.map(msg => msg.content).join(" ");
    
    // Build the prompt for the LLM judge
    const evaluationPrompt = `
${rule.customPromptTemplate}

CONVERSATION TO EVALUATE:
${conversation.map(msg => `${msg.role}: ${msg.content}`).join('\n')}

RESPONSE TO JUDGE:
${combinedResponse}

Please respond with either "PASS" or "FAIL" followed by a brief explanation.
`;

    console.log("🔍 CUSTOM_LLM_JUDGE DEBUG:");
    console.log("🔍 Rule:", rule.name);
    console.log("🔍 Custom prompt template:", rule.customPromptTemplate);
    console.log("🔍 Full evaluation prompt being sent to LLM:");
    console.log("🔍", evaluationPrompt);
    console.log("🔍 Conversation being evaluated:", conversation);
    console.log("🔍 Combined response:", combinedResponse);

    try {
      const llmResponse = await llmSendFunction(evaluationPrompt, [], [], false);
      
      console.log("🔍 LLM Response:", llmResponse);
      
      if (!llmResponse) {
        return {
          ruleId: rule.id,
          ruleName: rule.name,
          operator: rule.operator,
          passed: false,
          message: "LLM judge did not return a response",
          error: "No LLM response",
          expectedValue: rule.customPromptTemplate,
          actualValue: "No response",
        };
      }

      // Parse the LLM response to determine pass/fail
      const normalizedResponse = llmResponse.toLowerCase().trim();
      const passed = normalizedResponse.startsWith('pass');
      
      console.log("🔍 Normalized response:", normalizedResponse);
      console.log("🔍 Validation passed:", passed);
      
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed,
        message: passed 
          ? `✓ Custom LLM judge: ${llmResponse}`
          : `✗ Custom LLM judge: ${llmResponse}`,
        expectedValue: rule.customPromptTemplate,
        actualValue: llmResponse,
      };
    } catch (error) {
      console.error("🔍 Error in CUSTOM_LLM_JUDGE:", error);
      
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        operator: rule.operator,
        passed: false,
        message: `LLM judge error: ${error instanceof Error ? error.message : "Unknown error"}`,
        error: error instanceof Error ? error.message : "Unknown error",
        expectedValue: rule.customPromptTemplate,
        actualValue: "Error occurred",
      };
    }
  }

  /**
   * Helper to extract conversation data from LLM call messages
   */
  static parseConversationFromMessages(messagesJson: string): ConversationMessage[] {
    try {
      const messages = JSON.parse(messagesJson);
      if (!Array.isArray(messages)) {
        throw new Error("Messages must be an array");
      }
      
      return messages.map((msg: any) => ({
        role: msg.role,
        content: msg.content || "",
        tool_calls: msg.tool_calls,
        tool_call_id: msg.tool_call_id,
      }));
    } catch (error) {
      console.error("Error parsing conversation messages:", error);
      return [];
    }
  }

  /**
   * Get summary statistics for validation results
   */
  static getValidationSummary(results: ConversationValidationResults): {
    successRate: number;
    status: "PASSED" | "FAILED" | "PARTIAL";
    summary: string;
  } {
    const { totalRules, passedRules, failedRules } = results;
    const successRate = totalRules > 0 ? (passedRules / totalRules) * 100 : 0;
    
    let status: "PASSED" | "FAILED" | "PARTIAL";
    if (passedRules === totalRules) {
      status = "PASSED";
    } else if (passedRules === 0) {
      status = "FAILED";
    } else {
      status = "PARTIAL";
    }
    
    const summary = `${passedRules}/${totalRules} validations passed (${successRate.toFixed(1)}%)`;
    
    return { successRate, status, summary };
  }
}
